<template>
  <div>
    <div class="landing-page">
      <section class="landing-shell">
        <header class="landing-header">
          <h1 class="landing-title">
            {{ greetingLabel }},
            <span
              data-testid="landing-user-name"
              class="landing-title-accent"
              :style="accentTextStyle"
            >
              {{ userName || "User" }}
            </span>
          </h1>
          <p class="landing-subtitle">
            Here's a quick overview of your alerts, activities, and assigned work.
          </p>
        </header>

        <a-spin :spinning="spinner" :tip="spinnerTip">
          <section v-if="dashboardAlerts.length > 0" class="metric-grid">
            <article
              v-for="alertData in dashboardAlerts"
              :key="alertData.id"
              class="metric-card"
              :style="metricCardStyle"
              @click="generateReminderCsv(alertData.data, alertData.details.name);"
            >
              <div class="metric-card-copy">
                <p class="metric-label">{{ alertData.details.name }}</p>
                <h2 class="metric-value" :style="accentTextStyle">
                  {{ alertData.data.length }}
                </h2>
              </div>
              <div class="metric-icon-wrap" :style="getAlertIconWrapStyle(alertData.details)">
                <a-icon
                  :type="getAlertIconConfig(alertData.details).type"
                  :theme="getAlertIconConfig(alertData.details).theme"
                  :style="getAlertIconStyle(alertData.details)"
                  class="metric-icon"
                />
              </div>
            </article>
          </section>
        </a-spin>

        <section class="content-grid">
          <article class="content-panel content-panel-activities">
            <div class="content-panel-header">
              <div class="content-panel-icon" :style="accentSoftStyle">
                <i class="ni ni-collection"></i>
              </div>
              <h2>Recent Activities</h2>
            </div>

            <div v-if="recentActivities.length === 0" class="empty-state">
              No recent activities.
            </div>

            <div v-else class="activity-list">
              <div
                v-for="(userActivity, index) in recentActivities"
                :key="index"
                class="activity-item"
              >
                <span class="activity-action">{{ userActivity.activity.action }}</span>
                <router-link
                  v-if="userActivity.site"
                  :to="userActivity.site"
                  class="activity-link"
                  :style="accentTextStyle"
                >
                  {{ userActivity.activity.system_module }}
                </router-link>
                <span v-else class="activity-module">
                  {{ userActivity.activity.system_module }}
                </span>
              </div>
            </div>
          </article>

          <article class="content-panel content-panel-work">
            <div class="content-panel-header">
              <div class="content-panel-icon" :style="accentSoftStyle">
                <i class="ni ni-check-bold"></i>
              </div>
              <h2>My Work</h2>
            </div>

            <div class="work-list">
              <div class="work-tile">
                <span class="work-value" :style="accentTextStyle">
                  {{ pendingDocuments }}
                </span>
                <span class="work-label">Pending Documents</span>
                <span class="work-meta">Assigned to Me</span>
              </div>

              <div class="work-tile">
                <span class="work-value" :style="accentTextStyle">
                  {{ intransitDocuments }}
                </span>
                <span class="work-label">In-transit Documents</span>
                <span class="work-meta">Assigned to Me</span>
              </div>

              <div
                v-if="!hasMyWorkAlerts"
                class="empty-state empty-state-compact"
              >
                No work reminders enabled.
              </div>
            </div>
          </article>
        </section>
      </section>
    </div>

    <releases />

    <a-modal
      :title="reminderModalTitle"
      :visible="reminderModal"
      :maskClosable="false"
      @cancel="reminderModal = false;"
      width="70%"
      wrap-class-name="full-modal"
    >
      <template slot="footer">
        <button class="btn btn-success btn-sm align-bottom" @click.prevent="exportDataToCSV">
          Export to CSV
        </button>
        <button class="btn btn-sm align-bottom" key="back" @click="reminderModal = false;">
          Close
        </button>
      </template>
      <a-table
        rowKey="id"
        :columns="reminderColumns"
        :dataSource="reminderData"
        :pagination="true"
        size="small"
      ></a-table>
    </a-modal>
  </div>
</template>

<script>
import secureStorage from '../controllers/secureStorage.js'
import Releases from './Releases.vue'
import { mapState } from 'vuex'
import HTTPRequest from '../controllers/httpRequest.js'
import Papa from "papaparse";

const getNotificationStyle = type => {
  return {
    success: {
      color: 'rgba(0, 0, 0, 0.65)',
      border: '1px solid #b7eb8f',
      backgroundColor: '#f6ffed'
    },
    warning: {
      color: 'rgba(0, 0, 0, 0.65)',
      border: '1px solid #ffe58f',
      backgroundColor: '#fffbe6'
    },
    error: {
      color: 'rgba(0, 0, 0, 0.65)',
      border: '1px solid #ffa39e',
      backgroundColor: '#fff1f0'
    },
    info: {
      color: 'rgba(0, 0, 0, 0.65)',
      border: '1px solid #91d5ff',
      backgroundColor: '#e6f7ff'
    }
  }[type]
}

export default {
  constants: {
    BRAND_BLUE: '#0059ED',
    BRAND_BLUE_SOFT: 'rgba(0, 89, 237, 0.12)',
    ALERT_ICON_COLOR_MAP: {
      lowStockItems: {
        color: '#FAAD14',
        softColor: 'rgba(250, 173, 20, 0.14)'
      },
      overStockItems: {
        color: '#FAAD14',
        softColor: 'rgba(250, 173, 20, 0.14)'
      },
      negativeOrZeroInventory: {
        color: '#FAAD14',
        softColor: 'rgba(250, 173, 20, 0.14)'
      },
      zeroPriceItems: {
        color: '#FF4D4F',
        softColor: 'rgba(255, 77, 79, 0.14)'
      },
      zeroCostItems: {
        color: '#FF4D4F',
        softColor: 'rgba(255, 77, 79, 0.14)'
      },
      retailPriceBelowLandedCost: {
        color: '#FF4D4F',
        softColor: 'rgba(255, 77, 79, 0.14)'
      },
      pendingDocuments: {
        color: '#52C41A',
        softColor: 'rgba(82, 196, 26, 0.14)'
      },
      intransitDocuments: {
        color: '#52C41A',
        softColor: 'rgba(82, 196, 26, 0.14)'
      }
    },
    ALERT_ICON_MAP: {
      lowStockItems: { type: 'shopping-cart', theme: 'outlined' },
      overStockItems: { type: 'inbox', theme: 'outlined' },
      zeroPriceItems: { type: 'tag', theme: 'outlined' },
      zeroCostItems: { type: 'wallet', theme: 'outlined' },
      negativeOrZeroInventory: { type: 'warning', theme: 'outlined' },
      retailPriceBelowLandedCost: { type: 'line-chart', theme: 'outlined' },
      pendingDocuments: { type: 'file-text', theme: 'outlined' },
      intransitDocuments: { type: 'car', theme: 'outlined' }
    }
  },
  computed: {
    ...mapState(["endPoint", "validSites"]),
    greetingLabel() {
      const hour = new Date().getHours();
      if (hour < 12) {
        return "Good morning";
      }
      if (hour < 18) {
        return "Good afternoon";
      }
      return "Good evening";
    },
    accentColor() {
      return this.$options.constants.BRAND_BLUE;
    },
    accentSoftColor() {
      return this.$options.constants.BRAND_BLUE_SOFT;
    },
    accentTextStyle() {
      return {
        color: this.accentColor
      };
    },
    accentSoftStyle() {
      return {
        color: this.accentColor,
        backgroundColor: this.accentSoftColor
      };
    },
    metricCardStyle() {
      return {
        borderTop: `3px solid ${this.accentColor}`
      };
    },
    dashboardAlerts() {
      return this.alertDatas
        .filter(alertData => !['pendingDocuments', 'intransitDocuments'].includes(alertData.details.code))
        .slice(0, 5);
    },
    hasMyWorkAlerts() {
      return Boolean(this.userAlerts.pendingDocuments || this.userAlerts.intransitDocuments);
    }
  },
  mounted: async function () {
    var userId = secureStorage.getItem("userId");
    var user_activities = await HTTPRequest.getRequest(this.endPoint + "user_activities?user_id=" + userId, null);
    this.recentActivities = user_activities;
    this.userAlerts = secureStorage.getItem('userAlerts');
    var credentials = await HTTPRequest.getRequest(this.endPoint + 'settings/accountSettings?field_name=code' , null)
    this.accountAlerts = JSON.parse(credentials.value)
    this.system_alerts = await HTTPRequest.getRequest(this.endPoint + 'settings/get_alerts' , null)
    for (const [key, value] of Object.entries(this.userAlerts)) {
      if(value == true){
        this.getAlertDetails(key);
      }
    }
  },
  data() {
    return {
      reminderColumns: [],
      reminderData: [],
      reminderModalTitle: "",
      reminderModal: false,
      system_alerts: { alerts: [] },
      userName: secureStorage.getItem("userName"),
      recentActivities: [],
      userAlerts: {},
      accountAlerts: {},
      pendingDocuments: 0,
      intransitDocuments: 0,
      alertDatas: [],
      spinner: false,
      spinnerTip: "Loading reminders..."
    };
  },
  components: { Releases },
  methods: {
    getAlertIconConfig(alertDetails = {}) {
      const iconOverride = this.$options.constants.ALERT_ICON_MAP[alertDetails.code];
      const fallbackStyles = alertDetails.alert_styles || {};

      return {
        type: (iconOverride && iconOverride.type) || fallbackStyles.icon_type || 'alert',
        theme: (iconOverride && iconOverride.theme) || fallbackStyles.icon_theme || 'outlined'
      };
    },
    getAlertIconColorConfig(alertDetails = {}) {
      return this.$options.constants.ALERT_ICON_COLOR_MAP[alertDetails.code] || {
        color: this.accentColor,
        softColor: this.accentSoftColor
      };
    },
    getAlertIconStyle(alertDetails = {}) {
      return {
        color: this.getAlertIconColorConfig(alertDetails).color
      };
    },
    getAlertIconWrapStyle(alertDetails = {}) {
      return {
        backgroundColor: this.getAlertIconColorConfig(alertDetails).softColor
      };
    },
    formatColumnTitle(value) {
      return String(value)
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    exportDataToCSV() {
      const columns = Object.keys(this.reminderData[0])
      const csvdata = this.reminderData;

      var blob = new Blob([Papa.unparse(csvdata, { columns: columns, header: true })], { type: 'text/csv;charset=utf-8;' });
      var link = document.createElement("a");
      var url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", this.reminderModalTitle + '.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.$notification['success']({
        message: "Importing to CSV",
        duration: 5,
        description: "Data has been exported to CSV.",
        style: getNotificationStyle('error')
      });
    },
    generateReminderCsv(data, name) {
      if (data.length == 0) {
        this.$notification['error']({
          message: "Ooops!",
          duration: 5,
          description: "No data for " + name + ".",
          style: getNotificationStyle('error')
        });
        return;
      }

      this.reminderColumns = [];
      this.reminderData = data;
      let i = 0
      for (i in Object.keys(data[0])) {
        if (Object.keys(data[0])[i] == 'id') {
          this.reminderColumns.push({
            title: this.formatColumnTitle(Object.keys(data[0])[i]),
            dataIndex: Object.keys(data[0])[i],
            key: 'id',
            sorter: (a, b) => a.id - b.id,
            sortDirections: ['descend', 'ascend'],
            defaultSortOrder: 'descend'
          })
        } else {
          this.reminderColumns.push({
            title: this.formatColumnTitle(Object.keys(data[0])[i]),
            dataIndex: Object.keys(data[0])[i]
          })
        }
      }
      this.reminderModalTitle = name;
      this.reminderModal = true;
    },
    async getAlertDetails(code) {
      const alertDetails = this.system_alerts.alerts.find(x => x.code === code);
      const alertPayload = {
        alertDetails: alertDetails,
        userId: secureStorage.getItem("userId"),
        userSites: secureStorage.getItem("userSites").map(a => a.id)
      };

      this.spinner = true;
      if (typeof alertDetails != 'undefined') {
        const resAlert = await HTTPRequest.postRequest(this.endPoint + 'settings/process_query_alerts', null, alertPayload);
        const normalizedAlert = {
          id: alertDetails.id,
          details: alertDetails,
          data: resAlert || []
        };

        if (alertDetails.code === 'pendingDocuments') {
          this.pendingDocuments = normalizedAlert.data.length;
        }

        if (alertDetails.code === 'intransitDocuments') {
          this.intransitDocuments = normalizedAlert.data.length;
        }

        this.alertDatas.push(normalizedAlert);
      }
      this.spinner = false;
    }
  }
}
</script>

<style scoped>
.landing-page {
  min-height: 100%;
  padding: 96px 24px 32px;
  background: linear-gradient(180deg, #f4f7fb 0%, #eef2f7 100%);
}

.landing-shell {
  max-width: 1320px;
  margin: 0 auto;
}

.landing-header {
  margin-bottom: 22px;
}

.landing-title {
  margin: 0;
  color: #0e1b4d;
  font-size: clamp(2rem, 3vw, 2.45rem);
  font-weight: 800;
  letter-spacing: -0.03em;
}

.landing-title-accent {
  display: inline-block;
}

.landing-subtitle {
  margin: 8px 0 0;
  color: #6f81a0;
  font-size: 1rem;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 22px;
}

.metric-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 112px;
  padding: 18px 18px 16px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e3ebf5;
  border-radius: 12px;
  box-shadow: 0 16px 34px rgba(29, 53, 87, 0.06);
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.metric-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 18px 36px rgba(29, 53, 87, 0.1);
}

.metric-card-copy {
  min-width: 0;
}

.metric-label {
  margin: 0 0 10px;
  color: #7286a5;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.35;
}

.metric-value {
  margin: 0;
  color: #173564;
  font-size: clamp(2rem, 2.3vw, 2.55rem);
  font-weight: 800;
  line-height: 1;
}

.metric-icon-wrap {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #f7f9fc;
}

.metric-icon {
  font-size: 1.8rem;
}

.content-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(280px, 1fr);
  gap: 18px;
}

.content-panel {
  min-height: 360px;
  padding: 22px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e3ebf5;
  border-radius: 12px;
  box-shadow: 0 16px 34px rgba(29, 53, 87, 0.06);
}

.content-panel-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
}

.content-panel-header h2 {
  margin: 0;
  color: #0e1b4d;
  font-size: 1.5rem;
  font-weight: 800;
}

.content-panel-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  font-size: 1.35rem;
}

.activity-list {
  display: grid;
  gap: 16px;
  align-content: start;
}

.activity-item {
  color: #173564;
  font-size: 1.2rem;
  line-height: 1.35;
}

.activity-action {
  margin-right: 6px;
}

.activity-link,
.activity-module {
  color: #0e1b4d;
  font-weight: 700;
  text-decoration: none;
}

.activity-link:hover {
  text-decoration: underline;
}

.work-list {
  display: grid;
  gap: 16px;
}

.work-tile {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 118px;
  padding: 18px 20px;
  background: #f5f7fb;
  border: 1px solid #e7edf5;
  border-radius: 12px;
  text-align: center;
}

.work-value {
  margin-bottom: 4px;
  color: #173564;
  font-size: 2rem;
  font-weight: 800;
  line-height: 1;
}

.work-label {
  color: #173564;
  font-size: 1.18rem;
  font-weight: 700;
  line-height: 1.3;
}

.work-meta {
  margin-top: 2px;
  color: #607493;
  font-size: 1rem;
}

.empty-state {
  color: #8aa0c2;
  font-size: 1rem;
  line-height: 1.5;
}

.empty-state-compact {
  text-align: center;
}

.ant-spin-nested-loading,
.ant-spin-container {
  height: 100%;
}

@media (max-width: 1399.98px) {
  .metric-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 991.98px) {
  .landing-page {
    padding: 88px 16px 24px;
  }

  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .content-grid {
    grid-template-columns: 1fr;
  }

  .content-panel {
    min-height: auto;
  }
}

@media (max-width: 575.98px) {
  .landing-title {
    font-size: 1.85rem;
  }

  .metric-grid {
    grid-template-columns: 1fr;
  }

  .metric-card {
    min-height: 96px;
    padding: 16px;
  }

  .content-panel {
    padding: 18px;
  }

  .content-panel-header h2 {
    font-size: 1.3rem;
  }

  .activity-item {
    font-size: 1.05rem;
  }

  .work-label {
    font-size: 1.05rem;
  }
}
</style>
